import React, { useState, useEffect, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import { Atom, Flask, Broadcast, CircleNotch } from "@phosphor-icons/react";
import { http } from "../lib/api";

export default function SharedExperiment({ token }) {
  const [exp, setExp] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    http.get(`/experiments/shared/${token}`)
      .then((r) => setExp(r.data))
      .catch((e) => {
        console.warn("Geteiltes Experiment nicht ladbar:", e);
        setError(true);
      });
  }, [token]);

  const validSensors = useMemo(
    () => (exp?.sensor_data?.sensors || []).filter((s) => !s.error),
    [exp]
  );

  return (
    <div className="min-h-[100dvh] bg-bg text-white font-body">
      <div className="max-w-lg mx-auto p-4 pb-12">
        <header className="flex items-center gap-2 py-4">
          <Atom size={26} weight="duotone" className="text-volt" />
          <div>
            <div className="font-heading font-black text-xl tracking-tight leading-none">PHYSIK AI</div>
            <div className="text-[10px] text-muted uppercase tracking-[0.2em]">Geteiltes Experiment</div>
          </div>
        </header>

        {error && (
          <div data-testid="shared-not-found" className="text-center py-16">
            <Flask size={40} weight="duotone" className="text-muted mx-auto mb-3" />
            <p className="text-muted">Dieses Experiment existiert nicht mehr oder der Link ist ungültig.</p>
          </div>
        )}

        {!exp && !error && (
          <div className="flex justify-center py-16">
            <CircleNotch size={28} className="animate-spin text-volt" />
          </div>
        )}

        {exp && (
          <div data-testid="shared-experiment" className="space-y-4">
            <div className="bg-surface border border-white/10 rounded-xl p-5">
              <div className="flex items-center gap-2 text-volt mb-2">
                <Flask size={20} weight="duotone" />
                <span className="text-xs uppercase tracking-[0.2em] font-bold">Experiment</span>
              </div>
              <h1 className="font-heading text-2xl font-bold leading-tight">{exp.title}</h1>
              <p className="text-xs text-muted mt-1">
                {new Date(exp.created_at).toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" })}
                {" · "}
                {exp.mode === "pro" ? "Pro-Analyse" : "Standard-Analyse"}
              </p>
            </div>

            <div className="bg-volt/10 border border-volt/40 rounded-xl p-4">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-volthover mb-1">Frage</div>
              <p className="text-sm">{exp.question}</p>
            </div>

            {exp.sensor_data && exp.sensor_data.sensors && (
              <div className="bg-surface border border-white/10 rounded-xl p-4">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-muted mb-3">
                  <Broadcast size={14} weight="bold" className="text-volt" />
                  Messdaten ({exp.sensor_data.window?.duration} s)
                </div>
                <div className="space-y-3">
                  {validSensors.map((s) => (
                    <div key={s.id}>
                      <div className="text-sm font-semibold mb-1.5">{s.name}</div>
                      <div className="grid grid-cols-2 gap-2 mb-1.5">
                        {(s.display || []).map((d) => (
                          <div key={`${s.id}-${d.label}`} className="bg-elevated rounded-lg px-3 py-2">
                            <div className="text-[10px] uppercase tracking-wider text-muted font-bold">{d.label}</div>
                            <div className="text-sm font-semibold">{d.value}</div>
                          </div>
                        ))}
                      </div>
                      {s.interpretation && <p className="text-xs text-volthover">{s.interpretation}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-elevated border border-white/10 rounded-xl p-4">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted mb-2">Antwort der KI</div>
              <div className="markdown-body text-[15px]">
                <ReactMarkdown>{exp.ai_response}</ReactMarkdown>
              </div>
            </div>

            <a
              data-testid="shared-cta-btn"
              href="/"
              className="block w-full text-center bg-volt hover:bg-volthover active:scale-[0.98] transition-all rounded-xl py-4 font-bold"
            >
              Eigene Experimente starten – Physik AI öffnen
            </a>
            <p className="text-center text-xs text-muted">
              Erstellt mit Physik AI – Chat mit deiner Umgebung
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
